import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, api } from '@/api/client'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()

const friendshipStatusSchema = z
  .enum(['none', 'pending_outgoing', 'pending_incoming', 'accepted'])
  .optional()

export type FriendshipStatus =
  | 'none'
  | 'pending_outgoing'
  | 'pending_incoming'
  | 'accepted'

export interface FavoriteTeam {
  id: number
  name: string
  short_name?: string | null | undefined
  logo_url?: string | null | undefined
}

export interface UserRank {
  position?: number | null | undefined
  total_users?: number | null | undefined
  label?: string | null | undefined
}

export interface DirectoryUserStats {
  friends_count?: number | undefined
  poules_count?: number | undefined
  predictions_count?: number | undefined
  badges_count?: number | undefined
}

export interface DirectoryUser {
  id: number
  name: string
  display_name?: string | null | undefined
  first_name?: string | null | undefined
  last_name?: string | null | undefined
  profile_image?: string | null | undefined
  rank_score: number
  rank?: UserRank | null | undefined
  favorite_team?: FavoriteTeam | null | undefined
  friendship_status: FriendshipStatus
  stats?: DirectoryUserStats | null | undefined
}

export interface PaginatedMeta {
  current_page: number
  last_page: number
  total: number
  per_page?: number | undefined
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginatedMeta
}

export interface FriendsPaginatedResponse extends PaginatedResponse<DirectoryUser> {
  viewer_stats?: DirectoryUserStats | null | undefined
}

export interface FriendshipSummaryResponse {
  data: {
    friends_count: number
    pending_incoming_count: number
    pending_outgoing_count: number
    list_count: number
  }
}

export interface UserProfile {
  id: number
  display_name?: string | null | undefined
  name?: string | null | undefined
  profile_image?: string | null | undefined
  rank_score: number
  rank?: UserRank | null | undefined
  friends_count: number
  poules_count: number
  poules: UserProfilePoule[]
  favorite_team?: FavoriteTeam | null | undefined
  friendship_status: FriendshipStatus
}

export interface UserProfilePoule {
  id: number
  name: string
  image_url?: string | null | undefined
}

export interface FriendshipActionResponse {
  status?: string | undefined
  message?: string | undefined
  data: DirectoryUser
}

export interface UserReportResponse {
  message?: string | undefined
}

export type FriendshipActionKind = 'send' | 'accept' | 'dismiss' | 'revoke'

interface FriendshipActionInput {
  userId: number
  action: FriendshipActionKind
  currentStatus: FriendshipStatus
}

const favoriteTeamSchema = z
  .object({
    id: z.number(),
    name: nullableString,
    display_name: nullableString,
    short_name: nullableString,
    logo_url: nullableString,
  })
  .passthrough()

const rankSchema = z
  .object({
    position: nullableNumber,
    total_users: nullableNumber,
    label: nullableString,
    name: nullableString,
  })
  .passthrough()

const userStatsSchema = z
  .object({
    friends_count: nullableNumber,
    poules_count: nullableNumber,
    predictions_count: nullableNumber,
    badges_count: nullableNumber,
  })
  .passthrough()

const directoryUserSchema = z
  .object({
    id: z.number(),
    name: nullableString,
    display_name: nullableString,
    first_name: nullableString,
    last_name: nullableString,
    profile_image: nullableString,
    rank_score: nullableNumber,
    rank: rankSchema.nullable().optional(),
    favorite_team: favoriteTeamSchema.nullable().optional(),
    friendship_status: friendshipStatusSchema,
    stats: userStatsSchema.nullable().optional(),
  })
  .passthrough()

const paginatedMetaSchema = z
  .object({
    current_page: nullableNumber,
    last_page: nullableNumber,
    total: nullableNumber,
    per_page: nullableNumber,
  })
  .passthrough()

const paginatedUsersEnvelopeSchema = z
  .object({
    data: z.array(directoryUserSchema).optional().default([]),
    meta: paginatedMetaSchema.optional(),
    current_page: nullableNumber,
    last_page: nullableNumber,
    total: nullableNumber,
    per_page: nullableNumber,
    viewer_stats: userStatsSchema.nullable().optional(),
  })
  .passthrough()

const friendshipSummarySchema = z
  .object({
    data: z
      .object({
        friends_count: z.number(),
        pending_incoming_count: z.number(),
        pending_outgoing_count: z.number(),
        list_count: z.number(),
      })
      .passthrough(),
  })
  .passthrough()

const userProfilePouleSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    image_url: nullableString,
    thumbnail_url: nullableString,
    profile_url: nullableString,
  })
  .passthrough()

const userProfileSchema = z
  .object({
    id: z.number(),
    display_name: nullableString,
    name: nullableString,
    profile_image: nullableString,
    rank_score: nullableNumber,
    rank: rankSchema.nullable().optional(),
    friends_count: nullableNumber,
    poules_count: nullableNumber,
    poules: z.array(userProfilePouleSchema).optional().default([]),
    favorite_team: favoriteTeamSchema.nullable().optional(),
    friendship_status: friendshipStatusSchema,
  })
  .passthrough()

const wrappedUserProfileSchema = z
  .object({
    data: userProfileSchema,
  })
  .passthrough()

const friendshipActionResponseSchema = z
  .object({
    status: nullableString,
    message: nullableString,
    data: directoryUserSchema,
  })
  .passthrough()

const reportResponseSchema = z
  .object({
    message: nullableString,
  })
  .passthrough()

const FRIENDS_PAGE_SIZE = 25

async function fetchDirectoryUsers(page = 1): Promise<PaginatedResponse<DirectoryUser>> {
  const response = await api.get<unknown>(
    `/users?${new URLSearchParams({
      per_page: String(FRIENDS_PAGE_SIZE),
      page: String(page),
    }).toString()}`,
  )

  return parsePaginatedUsersResponse(response, 'Gebruikersdirectory kon niet worden geladen.')
}

async function fetchFriends(page = 1): Promise<FriendsPaginatedResponse> {
  const response = await api.get<unknown>(
    `/users/me/friends?${new URLSearchParams({
      per_page: String(FRIENDS_PAGE_SIZE),
      page: String(page),
    }).toString()}`,
  )

  const parsed = paginatedUsersEnvelopeSchema.safeParse(response)
  if (!parsed.success) {
    console.error('[friends] Invalid friends list payload', parsed.error.flatten())
    throw new Error('Vriendenlijst kon niet worden geladen.')
  }

  return {
    data: parsed.data.data.map(toDirectoryUser),
    meta: toPaginatedMeta(parsed.data),
    viewer_stats: parsed.data.viewer_stats ? toDirectoryUserStats(parsed.data.viewer_stats) : undefined,
  }
}

async function fetchFriendshipSummary(): Promise<FriendshipSummaryResponse> {
  const response = await api.get<unknown>('/users/me/friends/summary')
  const parsed = friendshipSummarySchema.safeParse(response)

  if (!parsed.success) {
    console.error('[friends] Invalid friendship summary payload', parsed.error.flatten())
    throw new Error('Vriendensamenvatting kon niet worden geladen.')
  }

  return parsed.data
}

async function fetchSearchUsers(
  query: string,
  page = 1,
): Promise<PaginatedResponse<DirectoryUser>> {
  const response = await api.get<unknown>(
    `/users/search?${new URLSearchParams({
      q: query,
      per_page: String(FRIENDS_PAGE_SIZE),
      page: String(page),
    }).toString()}`,
  )

  return parsePaginatedUsersResponse(response, 'Zoekresultaten konden niet worden geladen.')
}

async function fetchUserProfile(userId: number): Promise<UserProfile> {
  const response = await api.get<unknown>(`/users/${userId}`)
  const wrapped = wrappedUserProfileSchema.safeParse(response)
  if (wrapped.success) return toUserProfile(wrapped.data.data)

  const unwrapped = userProfileSchema.safeParse(response)
  if (unwrapped.success) return toUserProfile(unwrapped.data)

  console.error('[friends] Invalid user profile payload', {
    wrapped: wrapped.success ? null : wrapped.error.flatten(),
    unwrapped: unwrapped.success ? null : unwrapped.error.flatten(),
  })
  throw new Error('Profiel kon niet worden geladen.')
}

async function sendFriendshipAction(
  input: FriendshipActionInput,
): Promise<FriendshipActionResponse> {
  const path =
    input.action === 'send'
      ? `/users/${input.userId}/friend-request`
      : input.action === 'accept'
        ? `/users/${input.userId}/friend-request/accept`
        : input.action === 'dismiss'
          ? `/users/${input.userId}/friend-request/dismiss`
          : `/users/${input.userId}/friend-request/revoke`

  const response = await api.post<unknown>(path, {})
  const parsed = friendshipActionResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[friends] Invalid friendship action payload', parsed.error.flatten())
    throw new Error('Vriendschapsactie kon niet worden verwerkt.')
  }

  return {
    status: parsed.data.status ?? undefined,
    message: parsed.data.message ?? undefined,
    data: toDirectoryUser(parsed.data.data),
  }
}

async function reportUser(userId: number): Promise<UserReportResponse> {
  const response = await api.post<unknown>(`/users/${userId}/report`, {})
  const parsed = reportResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[friends] Invalid report response payload', parsed.error.flatten())
    throw new Error('Gebruiker kon niet worden gerapporteerd.')
  }

  return {
    message: parsed.data.message ?? undefined,
  }
}

export function useFriendsList(enabled = true) {
  return useInfiniteQuery({
    queryKey: ['friends', 'list'],
    queryFn: ({ pageParam }) => fetchFriends(Number(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useDirectoryUsers(enabled = true) {
  return useInfiniteQuery({
    queryKey: ['friends', 'directory'],
    queryFn: ({ pageParam }) => fetchDirectoryUsers(Number(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useSearchUsers(query: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['friends', 'search', query],
    queryFn: ({ pageParam }) => fetchSearchUsers(query, Number(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    enabled: enabled && query.trim().length > 0,
    staleTime: 15_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useFriendshipSummary(enabled = true) {
  return useQuery({
    queryKey: ['friends', 'summary'],
    queryFn: fetchFriendshipSummary,
    enabled,
    staleTime: 30_000,
  })
}

export function useUserProfile(userId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['friends', 'profile', userId],
    queryFn: () => fetchUserProfile(userId as number),
    enabled: userId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useFriendshipAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: sendFriendshipAction,
    onMutate: async (variables) => {
      const previousDirectory = queryClient.getQueriesData<
        InfiniteData<PaginatedResponse<DirectoryUser>>
      >({ queryKey: ['friends', 'directory'] })
      const previousSearch = queryClient.getQueriesData<
        InfiniteData<PaginatedResponse<DirectoryUser>>
      >({ queryKey: ['friends', 'search'] })
      const previousFriends = queryClient.getQueriesData<
        InfiniteData<FriendsPaginatedResponse>
      >({ queryKey: ['friends', 'list'] })
      const previousProfile = queryClient.getQueryData<UserProfile>([
        'friends',
        'profile',
        variables.userId,
      ])
      const previousSummary = queryClient.getQueryData<FriendshipSummaryResponse>([
        'friends',
        'summary',
      ])

      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['friends', 'directory'] }),
        queryClient.cancelQueries({ queryKey: ['friends', 'search'] }),
        queryClient.cancelQueries({ queryKey: ['friends', 'list'] }),
        queryClient.cancelQueries({ queryKey: ['friends', 'profile', variables.userId] }),
        queryClient.cancelQueries({ queryKey: ['friends', 'summary'] }),
      ])

      const nextStatus = getNextFriendshipStatus(variables.currentStatus, variables.action)

      queryClient.setQueriesData<InfiniteData<PaginatedResponse<DirectoryUser>>>(
        { queryKey: ['friends', 'directory'] },
        (current) => patchInfiniteUsersData(current, variables.userId, (user) => ({
          ...user,
          friendship_status: nextStatus,
        })),
      )

      queryClient.setQueriesData<InfiniteData<PaginatedResponse<DirectoryUser>>>(
        { queryKey: ['friends', 'search'] },
        (current) => patchInfiniteUsersData(current, variables.userId, (user) => ({
          ...user,
          friendship_status: nextStatus,
        })),
      )

      queryClient.setQueriesData<InfiniteData<FriendsPaginatedResponse>>(
        { queryKey: ['friends', 'list'] },
        (current) =>
          patchInfiniteUsersData(current, variables.userId, (user) => {
            if (nextStatus === 'accepted' || nextStatus === 'pending_incoming') {
              return { ...user, friendship_status: nextStatus }
            }
            return null
          }),
      )

      if (previousProfile) {
        queryClient.setQueryData<UserProfile>(['friends', 'profile', variables.userId], {
          ...previousProfile,
          friendship_status: nextStatus,
        })
      }

      if (previousSummary) {
        queryClient.setQueryData<FriendshipSummaryResponse>(['friends', 'summary'], {
          data: patchSummaryCounts(previousSummary.data, variables.action),
        })
      }

      return {
        previousDirectory,
        previousSearch,
        previousFriends,
        previousProfile,
        previousSummary,
      }
    },
    onError: (_error, variables, context) => {
      for (const [queryKey, data] of context?.previousDirectory ?? []) {
        queryClient.setQueryData(queryKey, data)
      }
      for (const [queryKey, data] of context?.previousSearch ?? []) {
        queryClient.setQueryData(queryKey, data)
      }
      for (const [queryKey, data] of context?.previousFriends ?? []) {
        queryClient.setQueryData(queryKey, data)
      }

      if (context?.previousProfile) {
        queryClient.setQueryData(['friends', 'profile', variables.userId], context.previousProfile)
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(['friends', 'summary'], context.previousSummary)
      }
    },
    onSuccess: (response, variables) => {
      queryClient.setQueriesData<InfiniteData<PaginatedResponse<DirectoryUser>>>(
        { queryKey: ['friends', 'directory'] },
        (current) => patchInfiniteUsersData(current, variables.userId, () => response.data),
      )
      queryClient.setQueriesData<InfiniteData<PaginatedResponse<DirectoryUser>>>(
        { queryKey: ['friends', 'search'] },
        (current) => patchInfiniteUsersData(current, variables.userId, () => response.data),
      )
      queryClient.setQueriesData<InfiniteData<FriendsPaginatedResponse>>(
        { queryKey: ['friends', 'list'] },
        (current) =>
          patchInfiniteUsersData(current, variables.userId, () => {
            if (
              response.data.friendship_status === 'accepted' ||
              response.data.friendship_status === 'pending_incoming'
            ) {
              return response.data
            }
            return null
          }),
      )

      const existingProfile = queryClient.getQueryData<UserProfile>([
        'friends',
        'profile',
        variables.userId,
      ])
      if (existingProfile) {
        queryClient.setQueryData<UserProfile>(['friends', 'profile', variables.userId], {
          ...existingProfile,
          display_name: response.data.display_name ?? existingProfile.display_name,
          name: response.data.name ?? existingProfile.name,
          profile_image: response.data.profile_image ?? existingProfile.profile_image,
          rank_score: response.data.rank_score,
          rank: response.data.rank ?? existingProfile.rank,
          favorite_team: response.data.favorite_team ?? existingProfile.favorite_team,
          friendship_status: response.data.friendship_status,
        })
      }
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['friends', 'directory'] }),
        queryClient.invalidateQueries({ queryKey: ['friends', 'search'] }),
        queryClient.invalidateQueries({ queryKey: ['friends', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['friends', 'summary'] }),
        queryClient.invalidateQueries({ queryKey: ['friends', 'profile', variables.userId] }),
      ])
    },
  })
}

export function useReportUser() {
  return useMutation({
    mutationFn: ({ userId }: { userId: number }) => reportUser(userId),
    retry: (count, error) => !(error instanceof ApiError && error.status === 422) && count < 1,
  })
}

export function getDisplayName(user?: {
  display_name?: string | null | undefined
  name?: string | null | undefined
} | null) {
  const displayName = user?.display_name?.trim()
  if (displayName) return displayName
  const fallbackName = user?.name?.trim()
  if (fallbackName) return fallbackName
  return 'Speler'
}

export function getFavoriteTeamName(team?: FavoriteTeam | null) {
  return team?.name?.trim() || team?.short_name?.trim() || 'Geen favoriet team'
}

function parsePaginatedUsersResponse(
  response: unknown,
  errorMessage: string,
): PaginatedResponse<DirectoryUser> {
  const parsed = paginatedUsersEnvelopeSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[friends] Invalid paginated users payload', parsed.error.flatten())
    throw new Error(errorMessage)
  }

  return {
    data: parsed.data.data.map(toDirectoryUser),
    meta: toPaginatedMeta(parsed.data),
  }
}

function toPaginatedMeta(
  raw: z.infer<typeof paginatedUsersEnvelopeSchema>,
): PaginatedMeta {
  return {
    current_page: raw.meta?.current_page ?? raw.current_page ?? 1,
    last_page: raw.meta?.last_page ?? raw.last_page ?? 1,
    total: raw.meta?.total ?? raw.total ?? raw.data.length,
    per_page: raw.meta?.per_page ?? raw.per_page ?? FRIENDS_PAGE_SIZE,
  }
}

function toDirectoryUser(raw: z.infer<typeof directoryUserSchema>): DirectoryUser {
  return {
    id: raw.id,
    name: raw.name?.trim() || raw.display_name?.trim() || 'Speler',
    display_name: raw.display_name ?? undefined,
    first_name: raw.first_name ?? undefined,
    last_name: raw.last_name ?? undefined,
    profile_image: raw.profile_image ?? undefined,
    rank_score: raw.rank_score ?? 0,
    rank: raw.rank
      ? {
          position: raw.rank.position ?? undefined,
          total_users: raw.rank.total_users ?? undefined,
          label: raw.rank.label ?? raw.rank.name ?? undefined,
        }
      : undefined,
    favorite_team: raw.favorite_team ? toFavoriteTeam(raw.favorite_team) : undefined,
    friendship_status: normalizeFriendshipStatus(raw.friendship_status),
    stats: raw.stats ? toDirectoryUserStats(raw.stats) : undefined,
  }
}

function toFavoriteTeam(raw: z.infer<typeof favoriteTeamSchema>): FavoriteTeam {
  return {
    id: raw.id,
    name: raw.name?.trim() || raw.display_name?.trim() || raw.short_name?.trim() || 'Team',
    short_name: raw.short_name ?? undefined,
    logo_url: raw.logo_url ?? undefined,
  }
}

function toDirectoryUserStats(raw: z.infer<typeof userStatsSchema>): DirectoryUserStats {
  return {
    friends_count: raw.friends_count ?? undefined,
    poules_count: raw.poules_count ?? undefined,
    predictions_count: raw.predictions_count ?? undefined,
    badges_count: raw.badges_count ?? undefined,
  }
}

function toUserProfile(raw: z.infer<typeof userProfileSchema>): UserProfile {
  return {
    id: raw.id,
    display_name: raw.display_name ?? undefined,
    name: raw.name ?? undefined,
    profile_image: raw.profile_image ?? undefined,
    rank_score: raw.rank_score ?? 0,
    rank: raw.rank
      ? {
          position: raw.rank.position ?? undefined,
          total_users: raw.rank.total_users ?? undefined,
          label: raw.rank.label ?? raw.rank.name ?? undefined,
        }
      : undefined,
    friends_count: raw.friends_count ?? 0,
    poules_count: raw.poules_count ?? 0,
    poules: raw.poules.map((poule) => ({
      id: poule.id,
      name: poule.name,
      image_url: poule.image_url ?? poule.thumbnail_url ?? poule.profile_url ?? undefined,
    })),
    favorite_team: raw.favorite_team ? toFavoriteTeam(raw.favorite_team) : undefined,
    friendship_status: normalizeFriendshipStatus(raw.friendship_status),
  }
}

function normalizeFriendshipStatus(value?: string): FriendshipStatus {
  if (value === 'pending_outgoing') return 'pending_outgoing'
  if (value === 'pending_incoming') return 'pending_incoming'
  if (value === 'accepted') return 'accepted'
  return 'none'
}

function getNextFriendshipStatus(
  currentStatus: FriendshipStatus,
  action: FriendshipActionKind,
): FriendshipStatus {
  if (action === 'send') return 'pending_outgoing'
  if (action === 'accept') return 'accepted'
  if (action === 'dismiss') return 'none'
  if (action === 'revoke' && currentStatus === 'pending_incoming') return 'none'
  if (action === 'revoke' && currentStatus === 'pending_outgoing') return 'none'
  if (action === 'revoke' && currentStatus === 'accepted') return 'none'
  return 'none'
}

function patchSummaryCounts(
  summary: FriendshipSummaryResponse['data'],
  action: FriendshipActionKind,
) {
  if (action === 'send') {
    return {
      ...summary,
      pending_outgoing_count: summary.pending_outgoing_count + 1,
    }
  }
  if (action === 'accept') {
    return {
      ...summary,
      friends_count: Math.max(0, summary.friends_count + 1),
      pending_incoming_count: Math.max(0, summary.pending_incoming_count - 1),
      list_count: Math.max(0, summary.list_count),
    }
  }
  if (action === 'dismiss') {
    return {
      ...summary,
      pending_incoming_count: Math.max(0, summary.pending_incoming_count - 1),
      list_count: Math.max(0, summary.list_count - 1),
    }
  }

  const nextFriendsCount = Math.max(0, summary.friends_count - 1)
  return {
    ...summary,
    friends_count: nextFriendsCount,
    pending_outgoing_count: Math.max(0, summary.pending_outgoing_count - 1),
    list_count:
      summary.friends_count > nextFriendsCount
        ? Math.max(0, summary.list_count - 1)
        : summary.list_count,
  }
}

function patchInfiniteUsersData<T extends PaginatedResponse<DirectoryUser>>(
  current: InfiniteData<T> | undefined,
  userId: number,
  updater: (user: DirectoryUser) => DirectoryUser | null,
): InfiniteData<T> | undefined {
  if (!current) return current

  return {
    ...current,
    pages: current.pages.map((page) => ({
      ...page,
      data: page.data.flatMap((user) => {
        if (user.id !== userId) return [user]
        const nextUser = updater(user)
        return nextUser ? [nextUser] : []
      }),
    })),
  }
}
